import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Calendar, Ruler, Weight, 
  Trophy, Instagram, Twitter, Facebook, Play,
  ChevronRight, Footprints, Hash, Globe, Star
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to resolve image URLs - /uploads/* paths are served from backend
const resolveImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/uploads/')) {
    return `${API_URL}/api${imagePath}`;
  }
  return imagePath;
};

// TikTok icon component
const TikTokIcon = ({ size = 24, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className={className}
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, accent = false }) => (
  <div className={`relative group overflow-hidden rounded-2xl p-5 transition-all duration-300 hover:scale-[1.02] ${
    accent 
      ? 'bg-gradient-to-br from-yellow-500/20 to-orange-500/10 border border-yellow-500/30' 
      : 'bg-white/5 border border-white/10 hover:border-white/20'
  }`}>
    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    <div className="relative">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={accent ? 'text-yellow-500' : 'text-white/40'} />
        <span className="text-xs uppercase tracking-wider text-white/40">{label}</span>
      </div>
      <p className={`text-xl font-semibold ${accent ? 'text-yellow-500' : 'text-white'}`}>
        {value}
      </p>
    </div>
  </div>
);

// Social Button Component
const SocialButton = ({ href, icon: Icon, label, color }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`group relative flex items-center gap-3 px-5 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-${color}-500/50 transition-all duration-300 hover:scale-[1.02]`}
  >
    <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-${color}-500/20 to-${color}-600/10 flex items-center justify-center`}>
      <Icon size={20} className={`text-${color}-400`} />
    </div>
    <span className="text-white/70 group-hover:text-white transition-colors">{label}</span>
    <ChevronRight size={16} className="text-white/30 group-hover:text-white/60 ml-auto transition-colors" />
  </a>
);

const PlayerProfilePage = () => {
  const { slug } = useParams();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    const fetchPlayer = async () => {
      try {
        const response = await fetch(`${API_URL}/api/players/slug/${slug}`);
        if (!response.ok) {
          throw new Error('Jugador no encontrado');
        }
        const data = await response.json();
        setPlayer(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlayer();
  }, [slug]);

  // Get all images for gallery
  const getAllImages = () => {
    if (!player) return [];
    const images = [];
    if (player.image) images.push(resolveImageUrl(player.image));
    if (player.image2) images.push(resolveImageUrl(player.image2));
    if (player.gallery && player.gallery.length > 0) {
      images.push(...player.gallery.map(img => resolveImageUrl(img)));
    }
    return images;
  };

  const images = getAllImages();

  // Extract YouTube video ID
  const getYoutubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-2 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin" />
          <p className="text-white/50 animate-pulse">Cargando perfil...</p>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
          <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mb-6">
            <span className="text-4xl">😕</span>
          </div>
          <h1 className="text-3xl font-semibold text-white mb-4">Jugador no encontrado</h1>
          <p className="text-white/60 mb-8 text-center max-w-md">
            El jugador que buscas no existe o no tiene página activa en este momento.
          </p>
          <Link 
            to="/jugadores"
            className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-black rounded-full font-semibold hover:shadow-lg hover:shadow-yellow-500/25 transition-all duration-300 hover:scale-105"
          >
            Ver todos los jugadores
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const youtubeId = getYoutubeId(player.video_url);

  return (
    <div className="min-h-screen bg-black">
      <Header />
      
      {/* Gradient Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/5 rounded-full blur-[100px]" />
      </div>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back button */}
          <Link 
            to="/jugadores"
            className="inline-flex items-center gap-2 text-white/50 hover:text-yellow-500 mb-8 transition-all duration-300 group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span>Volver a jugadores</span>
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Image Gallery */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative aspect-[3/4] rounded-3xl overflow-hidden bg-gradient-to-br from-white/10 to-white/5 shadow-2xl shadow-black/50 group">
                {/* Decorative corner */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-yellow-500/20 to-transparent" />
                
                <img
                  src={images[activeImage] || '/images/placeholder.jpg'}
                  alt={player.name}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                
                {/* Position badge */}
                <div className="absolute top-6 left-6">
                  <span className="px-4 py-2 bg-black/60 backdrop-blur-md rounded-full text-sm font-medium text-white border border-white/10">
                    {player.position}
                  </span>
                </div>
                
                {/* Shirt number */}
                {player.shirt_number && (
                  <div className="absolute top-6 right-6 w-14 h-14 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg shadow-yellow-500/25">
                    <span className="text-2xl font-bold text-black">{player.shirt_number}</span>
                  </div>
                )}
                
                {/* Video Play Button */}
                {player.video_url && youtubeId && (
                  <button
                    onClick={() => setShowVideo(true)}
                    className="absolute bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center hover:scale-110 transition-all duration-300 shadow-lg shadow-red-500/30 group/btn"
                  >
                    <Play size={28} className="text-white ml-1" fill="white" />
                    <div className="absolute inset-0 rounded-full bg-white/20 animate-ping opacity-75" />
                  </button>
                )}
                
                {/* Player name overlay on mobile */}
                <div className="absolute bottom-0 left-0 right-0 p-6 lg:hidden">
                  <p className="text-yellow-500 text-sm font-medium mb-1">{player.club || 'Agente Libre'}</p>
                  <h1 className="text-3xl font-bold text-white">{player.name}</h1>
                </div>
              </div>

              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImage(index)}
                      className={`flex-shrink-0 w-20 h-24 rounded-xl overflow-hidden transition-all duration-300 ${
                        index === activeImage 
                          ? 'ring-2 ring-yellow-500 ring-offset-2 ring-offset-black scale-105' 
                          : 'opacity-50 hover:opacity-80 grayscale hover:grayscale-0'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${player.name} ${index + 1}`}
                        className="w-full h-full object-cover object-top"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Player Info */}
            <div className="space-y-8 relative">
              {/* Name & Position - Hidden on mobile (shown in image overlay) */}
              <div className="hidden lg:block">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex items-center gap-2 px-4 py-2 bg-yellow-500/10 rounded-full border border-yellow-500/30">
                    <MapPin size={14} className="text-yellow-500" />
                    <span className="text-yellow-500 text-sm font-medium">{player.club || 'Agente Libre'}</span>
                  </div>
                  {player.country && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10">
                      <Globe size={14} className="text-white/50" />
                      <span className="text-white/70 text-sm">{player.country}</span>
                    </div>
                  )}
                </div>
                <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-2 tracking-tight">
                  {player.name}
                </h1>
                <div className="flex items-center gap-4 mt-4">
                  <span className="text-xl text-white/50">{player.position}</span>
                  {player.is_featured && (
                    <span className="flex items-center gap-1 text-yellow-500 text-sm">
                      <Star size={14} fill="currentColor" />
                      Destacado
                    </span>
                  )}
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {player.age && (
                  <StatCard icon={Calendar} label="Edad" value={`${player.age} años`} />
                )}
                {player.height && (
                  <StatCard icon={Ruler} label="Altura" value={player.height} />
                )}
                {player.weight && (
                  <StatCard icon={Weight} label="Peso" value={`${player.weight} kg`} />
                )}
                {player.dominant_foot && (
                  <StatCard icon={Footprints} label="Pie dominante" value={player.dominant_foot} />
                )}
                {player.country && (
                  <StatCard icon={Globe} label="Nacionalidad" value={player.country} />
                )}
                {player.secondary_nationality && (
                  <StatCard icon={Globe} label="2da Nacionalidad" value={player.secondary_nationality} />
                )}
              </div>

              {/* Biography */}
              {player.biography && (
                <div className="relative">
                  <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-yellow-500 to-orange-500 rounded-full" />
                  <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                      <span className="text-yellow-500 text-sm">📖</span>
                    </span>
                    Sobre {player.name.split(' ')[0]}
                  </h2>
                  <p className="text-white/60 leading-relaxed whitespace-pre-line pl-1">
                    {player.biography}
                  </p>
                </div>
              )}

              {/* Social Media */}
              {(player.instagram || player.twitter || player.tiktok || player.facebook) && (
                <div className="space-y-3">
                  <h3 className="text-sm uppercase tracking-wider text-white/40 mb-4">Redes Sociales</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {player.instagram && (
                      <a
                        href={player.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                          <Instagram size={20} className="text-white" />
                        </div>
                        <span className="text-white/70 group-hover:text-white transition-colors">Instagram</span>
                        <ChevronRight size={16} className="text-white/30 ml-auto group-hover:translate-x-1 transition-transform" />
                      </a>
                    )}
                    {player.twitter && (
                      <a
                        href={player.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-sky-500/10 border border-sky-500/20 hover:border-sky-500/40 transition-all duration-300"
                      >
                        <div className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center">
                          <Twitter size={20} className="text-white" />
                        </div>
                        <span className="text-white/70 group-hover:text-white transition-colors">Twitter</span>
                        <ChevronRight size={16} className="text-white/30 ml-auto group-hover:translate-x-1 transition-transform" />
                      </a>
                    )}
                    {player.tiktok && (
                      <a
                        href={player.tiktok}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-white/30 transition-all duration-300"
                      >
                        <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center border border-white/20">
                          <TikTokIcon size={20} className="text-white" />
                        </div>
                        <span className="text-white/70 group-hover:text-white transition-colors">TikTok</span>
                        <ChevronRight size={16} className="text-white/30 ml-auto group-hover:translate-x-1 transition-transform" />
                      </a>
                    )}
                    {player.facebook && (
                      <a
                        href={player.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-500/10 border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300"
                      >
                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                          <Facebook size={20} className="text-white" />
                        </div>
                        <span className="text-white/70 group-hover:text-white transition-colors">Facebook</span>
                        <ChevronRight size={16} className="text-white/30 ml-auto group-hover:translate-x-1 transition-transform" />
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Career History */}
      {player.club_history && player.club_history.length > 0 && (
        <section className="py-20 relative">
          {/* Section background */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-yellow-500/5 to-transparent" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="flex items-center gap-4 mb-12">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center shadow-lg shadow-yellow-500/25">
                <span className="text-2xl">⚽</span>
              </div>
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white">
                  Trayectoria
                </h2>
                <p className="text-white/50 mt-1">Clubes donde dejó su huella</p>
              </div>
            </div>
            
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-yellow-500 via-orange-500 to-yellow-500/20" />
              
              <div className="space-y-8">
                {player.club_history.map((club, index) => (
                  <div 
                    key={index}
                    className={`relative flex items-center gap-6 lg:gap-12 ${
                      index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                    }`}
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-8 lg:left-1/2 w-4 h-4 -translate-x-1/2 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full shadow-lg shadow-yellow-500/50 z-10">
                      <div className="absolute inset-0 rounded-full bg-yellow-500 animate-ping opacity-25" />
                    </div>
                    
                    {/* Card */}
                    <div className={`ml-16 lg:ml-0 lg:w-[calc(50%-3rem)] ${
                      index % 2 === 0 ? 'lg:pr-0' : 'lg:pl-0'
                    }`}>
                      <div className="group relative bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-6 border border-white/10 hover:border-yellow-500/30 transition-all duration-500 hover:shadow-xl hover:shadow-yellow-500/10">
                        {/* Hover gradient */}
                        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-yellow-500/10 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        
                        <div className="relative flex items-center gap-5">
                          {/* Club logo */}
                          <div className="w-20 h-20 flex-shrink-0 bg-white rounded-2xl overflow-hidden flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-500">
                            {club.club_logo ? (
                              <img
                                src={resolveImageUrl(club.club_logo)}
                                alt={club.club_name}
                                className="w-14 h-14 object-contain"
                              />
                            ) : (
                              <div className="text-neutral-400 text-3xl font-bold">
                                {club.club_name?.charAt(0)}
                              </div>
                            )}
                          </div>
                          
                          {/* Club info */}
                          <div className="flex-1">
                            <h3 className="text-xl font-semibold text-white group-hover:text-yellow-500 transition-colors">
                              {club.club_name}
                            </h3>
                            <div className="flex items-center gap-2 mt-2">
                              <span className="px-3 py-1 bg-white/10 rounded-full text-sm text-white/70">
                                {club.year_start || '?'} - {club.year_end || 'Presente'}
                              </span>
                              {!club.year_end && (
                                <span className="px-3 py-1 bg-green-500/20 rounded-full text-sm text-green-400 font-medium">
                                  Actual
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Spacer for alternating layout on desktop */}
                    <div className="hidden lg:block lg:w-[calc(50%-3rem)]" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* National Team History - Selección Nacional */}
      {player.national_team_history && player.national_team_history.length > 0 && (
        <section className="py-24 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-sky-500/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
          </div>
          
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-sky-400 via-sky-500 to-blue-600 mb-6 shadow-2xl shadow-sky-500/30">
                <span className="text-4xl">🇺🇾</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-3">
                Selección Nacional
              </h2>
              <p className="text-white/40 text-lg">
                {player.national_team_history.length} convocatoria{player.national_team_history.length > 1 ? 's' : ''} con la Celeste
              </p>
            </div>
            
            {/* National Team Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {player.national_team_history.map((entry, index) => {
                const isMayor = entry.team_type === 'Mayor';
                
                return (
                  <div 
                    key={index}
                    className="group relative"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className={`relative p-6 rounded-2xl border transition-all duration-500 hover:-translate-y-2 ${
                      isMayor 
                        ? 'bg-gradient-to-br from-sky-500/20 to-blue-500/10 border-sky-500/30 hover:border-sky-400/50 hover:shadow-xl hover:shadow-sky-500/20' 
                        : 'bg-gradient-to-br from-white/10 to-white/5 border-white/10 hover:border-white/30 hover:shadow-xl hover:shadow-white/10'
                    }`}>
                      {/* Flag badge */}
                      <div className="absolute -top-3 -right-3 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-2xl">
                        🇺🇾
                      </div>
                      
                      {/* Content */}
                      <div className="pr-8">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-lg font-bold ${isMayor ? 'text-sky-400' : 'text-white'}`}>
                            {entry.team_type}
                          </span>
                          {entry.is_friendly && (
                            <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 text-xs rounded-full">
                              Amistoso
                            </span>
                          )}
                        </div>
                        
                        {entry.year && (
                          <div className="text-white/50 text-sm mb-2">
                            {entry.year}
                          </div>
                        )}
                        
                        {entry.description && (
                          <p className="text-white/70 text-sm">
                            {entry.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Achievements - Vitrina de Trofeos */}
      {player.achievements && player.achievements.length > 0 && (
        <section className="py-24 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-yellow-500/5 via-transparent to-transparent rounded-full" />
          </div>
          
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600 mb-6 shadow-2xl shadow-yellow-500/30">
                <Trophy className="text-black" size={36} />
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-3">
                Palmarés
              </h2>
              <p className="text-white/40 text-lg">
                {player.achievements.length} títulos conquistados
              </p>
            </div>
            
            {/* Trophy Showcase */}
            <div className="relative">
              {/* Decorative line */}
              <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-yellow-500/30 to-transparent" />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {player.achievements.map((achievement, index) => {
                  // Determine trophy type based on keywords
                  const isLibertadores = achievement.toLowerCase().includes('libertadores');
                  const isWorldCup = achievement.toLowerCase().includes('mundial') || achievement.toLowerCase().includes('world');
                  const isInternational = isLibertadores || achievement.toLowerCase().includes('sudamericana') || achievement.toLowerCase().includes('recopa');
                  const isLeague = achievement.toLowerCase().includes('liga') || achievement.toLowerCase().includes('campeonato') || achievement.toLowerCase().includes('torneo');
                  
                  return (
                    <div 
                      key={index}
                      className="group relative"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className={`relative p-6 rounded-2xl border transition-all duration-500 hover:-translate-y-2 ${
                        isLibertadores 
                          ? 'bg-gradient-to-br from-yellow-500/20 via-amber-500/10 to-yellow-600/20 border-yellow-400/40 hover:border-yellow-400/60 hover:shadow-xl hover:shadow-yellow-500/20'
                          : isInternational
                          ? 'bg-gradient-to-br from-amber-500/15 to-orange-500/10 border-amber-500/30 hover:border-amber-400/50 hover:shadow-lg hover:shadow-amber-500/15'
                          : 'bg-white/[0.03] border-white/10 hover:border-white/20 hover:bg-white/[0.05]'
                      }`}>
                        
                        {/* Trophy Icon */}
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform duration-500 group-hover:scale-110 ${
                          isLibertadores 
                            ? 'bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg shadow-yellow-500/30'
                            : isInternational
                            ? 'bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg shadow-amber-500/25'
                            : 'bg-gradient-to-br from-white/20 to-white/10'
                        }`}>
                          {isLibertadores ? (
                            <span className="text-2xl">🏆</span>
                          ) : isInternational ? (
                            <span className="text-2xl">🥇</span>
                          ) : isLeague ? (
                            <span className="text-2xl">🏅</span>
                          ) : (
                            <Star size={24} className={isInternational ? 'text-black' : 'text-white/60'} />
                          )}
                        </div>
                        
                        {/* Achievement Text */}
                        <h3 className={`font-semibold text-lg leading-snug ${
                          isLibertadores ? 'text-yellow-400' : isInternational ? 'text-amber-400' : 'text-white'
                        }`}>
                          {achievement.split('(')[0].trim()}
                        </h3>
                        
                        {/* Club name if present */}
                        {achievement.includes('(') && (
                          <p className="text-white/40 text-sm mt-2">
                            {achievement.match(/\(([^)]+)\)/)?.[1]}
                          </p>
                        )}
                        
                        {/* Shine effect for top achievements */}
                        {isLibertadores && (
                          <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                            <div className="absolute -inset-full bg-gradient-to-r from-transparent via-yellow-400/10 to-transparent skew-x-12 group-hover:animate-shine" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Stats summary */}
            <div className="mt-16 flex justify-center">
              <div className="inline-flex items-center gap-8 px-8 py-4 rounded-full bg-white/5 border border-white/10">
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-500">{player.achievements.filter(a => a.toLowerCase().includes('libertadores') || a.toLowerCase().includes('sudamericana') || a.toLowerCase().includes('recopa')).length}</p>
                  <p className="text-xs text-white/40 uppercase tracking-wider">Internacionales</p>
                </div>
                <div className="w-px h-8 bg-white/20" />
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{player.achievements.filter(a => !a.toLowerCase().includes('libertadores') && !a.toLowerCase().includes('sudamericana') && !a.toLowerCase().includes('recopa')).length}</p>
                  <p className="text-xs text-white/40 uppercase tracking-wider">Locales</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-[2rem] overflow-hidden">
            {/* Background with blur effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 to-neutral-950" />
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
            
            {/* Glow effects */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-yellow-500/20 rounded-full blur-[100px]" />
            <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-yellow-600/15 rounded-full blur-[100px]" />
            
            {/* Content */}
            <div className="relative px-8 py-16 sm:px-16 sm:py-20">
              <div className="flex flex-col lg:flex-row items-center gap-10">
                {/* Left side - Text */}
                <div className="flex-1 text-center lg:text-left">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-sm font-medium mb-6">
                    Representación profesional
                  </span>
                  <h3 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
                    ¿Interesado en<br />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">{player.name.split(' ')[0]}</span>?
                  </h3>
                  <p className="text-white/50 text-lg max-w-md">
                    Contacta con FARO Sports para información detallada sobre este jugador y otros talentos de nuestra agencia.
                  </p>
                </div>
                
                {/* Right side - CTA */}
                <div className="flex flex-col items-center lg:items-end gap-4">
                  <Link
                    to="/#contacto"
                    className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-500 text-black font-semibold rounded-full overflow-hidden transition-all duration-300 hover:shadow-2xl hover:shadow-yellow-500/30"
                  >
                    <span className="relative z-10">Contactar ahora</span>
                    <ChevronRight size={20} className="relative z-10 group-hover:translate-x-1 transition-transform" />
                    <div className="absolute inset-0 bg-gradient-to-r from-yellow-300 to-amber-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                  <p className="text-white/30 text-sm">
                    Respuesta en menos de 24h
                  </p>
                </div>
              </div>
            </div>
            
            {/* Bottom accent line */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500/0 via-yellow-500 to-yellow-500/0" />
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {showVideo && youtubeId && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
        >
          <div className="relative w-full max-w-5xl aspect-video" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -top-12 right-0 flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <span>Cerrar</span>
              <span className="text-2xl">×</span>
            </button>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/50">
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
                title="Video del jugador"
                className="w-full aspect-video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PlayerProfilePage;
